/*
 * HomeFixer360 - Presale Checkout Page
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 * 
 * This software is proprietary and confidential. Unauthorized copying, distribution,
 * modification, or use is strictly prohibited.
 * 
 * For licensing inquiries: guy@ideaguybrands.com
 */

import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, DollarSign, Zap, Shield, Clock, Star, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Navigation from '../components/landing/Navigation';

const PresaleCheckout = () => {
  const [selectedPackage, setSelectedPackage] = useState('');
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const presalePackages = [
    {
      id: 'basic_presale',
      name: 'Basic Presale',
      originalPrice: 897, // 3 months at $299
      presalePrice: 597, // 3 months at $199
      savings: 300,
      setupFee: 150, // 50% off $300 (regular $300)
      totalSavings: 450,
      features: [
        '50 Satellite Measurements/month',
        'Up to 3 user accounts',
        'InsureTrax: 20 claims/month',
        'Basic PDF Reports',
        'Email Support',
        'Mobile App Access',
        'Beta Access Until Jan. 15, 2026',
        '60-Day Money Back Guarantee'
      ],
      popular: false
    },
    {
      id: 'professional_presale',
      name: 'Professional Presale',
      originalPrice: 1497, // 3 months at $499
      presalePrice: 997, // 3 months at $332.33
      savings: 500,
      setupFee: 250, // 50% off $500 (regular $500)
      totalSavings: 750,
      features: [
        '250 Satellite Measurements/month',
        'Up to 7 user accounts',
        'InsureTrax: 50 claims/month',
        'Advanced PDF Reports',
        'Priority Support',
        'Mobile App Access',
        'API Access',
        'Custom Branding',
        'Beta Access Until Jan. 15, 2026',
        '60-Day Money Back Guarantee'
      ],
      popular: true
    },
    {
      id: 'elite_presale',
      name: 'Elite Presale',
      originalPrice: 2997, // 3 months at $999
      presalePrice: 2322, // 3 months at $774
      savings: 675,
      setupFee: 375, // 50% off $750 (regular $750)
      totalSavings: 1050,
      features: [
        'Unlimited Satellite Measurements',
        'Unlimited user accounts',
        'InsureTrax: UNLIMITED claims',
        'Premium PDF Reports',
        'White-Label Options',
        'Dedicated Account Manager',
        'API Access & Integrations',
        'Custom Training',
        'Priority Phone Support',
        'Beta Access Until Jan. 15, 2026',
        '60-Day Money Back Guarantee'
      ],
      popular: false
    }
  ];

  const handleInputChange = (field, value) => {
    setCustomerInfo(prev => ({ ...prev, [field]: value }));
  };

  const initiatePayment = async () => {
    if (!selectedPackage) {
      alert('Please select a package');
      return;
    }

    if (!customerInfo.firstName || !customerInfo.lastName || !customerInfo.email) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: selectedPackage,
          customer_email: customerInfo.email,
          customer_name: `${customerInfo.firstName} ${customerInfo.lastName}`,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;

    } catch (error) {
      console.error('Payment error:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <Navigation />
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Presale Checkout</h1>
            <p className="text-gray-600">Secure your presale spot with massive savings</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Presale Banner */}
        <div className="bg-gradient-to-r from-red-500 via-orange-500 to-yellow-400 rounded-2xl p-1 mb-8">
          <div className="bg-gradient-to-br from-red-600 to-orange-600 rounded-xl p-6 text-white text-center">
            <h2 className="text-2xl font-black mb-2">🔥 LIMITED TIME PRESALE EXCLUSIVE!</h2>
            <p className="text-yellow-200 font-semibold">Only 50 Beta Testers • Presale Ends February 28, 2026 • Save up to $925</p>
            <div className="bg-blue-600 text-white rounded-lg p-2 mt-3 text-sm font-bold">
              📅 Beta: Now – Feb. 28 • Launch: Mar 1, 2026 • Presale Ends: Feb 28
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-8">
          {presalePackages.map((pkg, index) => (
            <div key={pkg.id} className={`relative bg-white rounded-2xl shadow-xl border-2 p-6 ${
              selectedPackage === pkg.id ? 'border-blue-500 ring-4 ring-blue-200' : 'border-gray-200'
            } ${pkg.popular ? 'ring-4 ring-orange-200 border-orange-400' : ''}`}>
              
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                    MOST POPULAR
                  </div>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                
                {/* Pricing */}
                <div className="mb-4">
                  <div className="text-3xl font-black text-gray-900">${pkg.presalePrice + pkg.setupFee}</div>
                  <div className="text-sm text-gray-500 line-through">${pkg.originalPrice + 500}</div>
                  <div className="text-green-600 font-bold">Save ${pkg.totalSavings}</div>
                </div>

                {/* Breakdown */}
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <div className="flex justify-between">
                    <span>Setup Fee (50% off):</span>
                    <span>${pkg.setupFee}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>3 Months Service:</span>
                    <span>${pkg.presalePrice}</span>
                  </div>
                  <div className="border-t mt-2 pt-2 flex justify-between font-bold">
                    <span>Total:</span>
                    <span>${pkg.presalePrice + pkg.setupFee}</span>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-3 mb-6">
                {pkg.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Select Button */}
              <Button
                onClick={() => setSelectedPackage(pkg.id)}
                className={`w-full py-3 rounded-lg font-semibold transition-all ${
                  selectedPackage === pkg.id
                    ? 'bg-blue-600 text-white'
                    : pkg.popular
                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                {selectedPackage === pkg.id ? 'Selected' : 'Select Package'}
              </Button>
            </div>
          ))}
        </div>

        {/* Usage Limits & Important Information */}
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6 mb-8 max-w-4xl mx-auto">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Important Usage Limits & Notifications
          </h3>
          <div className="space-y-2 text-sm text-gray-700">
            <p><strong>Monthly Limits:</strong> Each plan has monthly limits for InsureTrax claims and Lead Generation leads as shown above.</p>
            <p><strong>Automatic Notifications:</strong> You'll receive an email alert when you're within 10 claims or 10 leads of your monthly limit.</p>
            <p><strong>Reaching Your Limit:</strong> If you reach your monthly limit, you must upgrade to the next tier or that feature will be unusable until your next billing cycle.</p>
            <p><strong>No Rollover:</strong> Unused claims or leads do NOT roll over to the next month. Limits reset at the start of each billing cycle.</p>
          </div>
        </div>

        {/* Customer Information */}
        {selectedPackage && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Customer Information</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={customerInfo.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="John"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={customerInfo.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Smith"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="john@company.com"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="company">Company Name</Label>
                <Input
                  id="company"
                  value={customerInfo.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  placeholder="Your Home Service Company"
                />
              </div>
            </div>
          </div>
        )}

        {/* Payment Summary & Benefits */}
        {selectedPackage && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Summary */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Order Summary</h3>
                {(() => {
                  const pkg = presalePackages.find(p => p.id === selectedPackage);
                  return (
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span>{pkg?.name}</span>
                        <span>${pkg?.presalePrice + pkg?.setupFee}</span>
                      </div>
                      <div className="flex justify-between text-green-600 font-semibold">
                        <span>Total Savings:</span>
                        <span>-${pkg?.totalSavings}</span>
                      </div>
                      <div className="border-t pt-3 flex justify-between text-lg font-bold">
                        <span>Total:</span>
                        <span>${pkg?.presalePrice + pkg?.setupFee}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Benefits */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Presale Benefits</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Gift className="w-4 h-4 text-green-500" />
                    <span>50% OFF Setup Fee (Save $250)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Star className="w-4 h-4 text-blue-500" />
                    <span>FREE 30-Day Beta Access</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-orange-500" />
                    <span>60-Day Money Back Guarantee</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-blue-500" />
                    <span>Beta Jan 2026 • Launch Feb 2026</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Checkout Button */}
            <div className="mt-8 text-center">
              <Button
                onClick={initiatePayment}
                disabled={!selectedPackage || isSubmitting || !customerInfo.firstName || !customerInfo.lastName || !customerInfo.email}
                className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white px-12 py-4 text-lg font-bold rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
              >
                {isSubmitting ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </div>
                ) : (
                  <>
                    <Zap className="w-5 h-5 inline mr-2" />
                    Secure My Presale Spot
                  </>
                )}
              </Button>
              
              <p className="text-xs text-gray-600 mt-4">
                Secure payment powered by Stripe • 256-bit SSL encryption
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PresaleCheckout;